import React from "react";

import { useClass } from "@breezerr";
import { filterTinaMarkdownContentParagraphs } from "@brz-utils/tina/filter.paragraphs";

import { TinaMarkdown, TinaMarkdownContent } from 'tinacms/dist/rich-text';

import { Img, ImgProps } from "./img";
import { TextLink } from "./text.link";

import { RichCurrentYear } from "./rich.current.year";
import { Button, ButtonBase } from "@mui/material";
import { newLinesToBreaks } from "@brz-utils/newlines.to.breaks";
import { dateFormat } from "@brz-utils/date.format";


export type PostPreviewDataModel = {
    thumbnail: string;
    cover: string;
    updatedDate: string;
    headingRich: TinaMarkdownContent;
    excerptText: string;
    author: {
        name: string;
        avatar: string;
        settings: { seo: { url: string } }
    }
    category: {
        name: string;
        settings: { seo: { url: string } }
    }
    settings: { seo: { url: string } }
    readtime: {
        text: string;
        minutes: number;
        time: number;
        words: number;
    } | null
};

type Props = {
    data: PostPreviewDataModel;
    ImageProps?: ImgProps;
};

export const PostPreview = (props: Props) => {
    const post = props.data;
    const author = post.author;

    return (
        <article className={useClass('d-flex flex-column align-items-start')}>
            <TextLink 
                className={useClass('d-flex align-items-center justify-content-center w-100%')}
                href={post.settings?.seo.url}
            >
                <Img 
                    className={useClass('d-block w-100% h-auto mb-2rem radius-1rem')} 
                    src={post.thumbnail || post.cover} 
                    width={350} 
                    height={350} 
                    mode="fill" 
                    style={{ objectFit: 'cover' }}
                    {...props.ImageProps}
                />
            </TextLink>

            <small className={useClass('d-block mb-1rem')}>Last updated { new Date(post.updatedDate).toLocaleString('en-US', dateFormat) }</small>

            <h2 className={`h5 ${useClass('mb-1rem')}`}>
                <TextLink href={post.settings?.seo.url}>
                    <TinaMarkdown 
                        content={filterTinaMarkdownContentParagraphs(post.headingRich)}
                        components={{
                            CurrentYear: RichCurrentYear,
                            Break: (props: any) => {
                                return <>{new Array(props.size).fill(null).map((_, index) => (<br key={index} />))}</>
                            },
                        }}
                    />
                </TextLink>
            </h2>

            {author ? (
                <ButtonBase 
                    className={useClass('d-flex align-items-center pl-0.4rem py-0.4rem pr-2rem radius-2rem -ml-0.5rem mb-1.5rem')} 
                    href={author.settings?.seo.url}
                    component={TextLink}
                >
                    <div className={useClass('w-3rem h-3rem radius-100% bg-color-grey:50 mr-0.8rem')}>
                        <Img className={useClass('radius-100%')} src={author.avatar} width={30} height={30} />
                    </div>
                    <strong className={useClass('text-size-1.4rem')}>{ author.name }</strong>
                </ButtonBase>
            ) : null}

            <div className={useClass('text-size-1.4rem mb-2rem')} dangerouslySetInnerHTML={{ __html: newLinesToBreaks(post.excerptText) }} />

            {post.category ? (
                <Button
                    className={useClass('mt-auto max-h-2rem! text-size-1.2rem! px-1rem!')}
                    size="small"
                    variant="contained"
                    color="inherit"
                    href={post.category.settings.seo.url}
                    component={TextLink}
                >{ post.category.name }</Button>
            ) : null}
        </article>
    );
};